
import { useLocation, useMap, useOrgSettings } from "@/services";
import { computed, ComputedRef, defineComponent, onMounted, ref } from "vue";

import mapboxgl, {
  AnyLayer,
  AnySourceData,
  LngLatBoundsLike,
  Map,
  MapboxOptions,
  Marker,
} from "mapbox-gl";
import { GeometryLocation } from "@/types/location/GeometryLocation";
import { bbox } from "@turf/turf";
import { resolveBaseMapStyle } from "@/util/helpers";

export default defineComponent({
  name: "PrintMap",
  data: () => ({
    currentHref: window.location.href,
  }),
  watch: {
    $route() {
      this.currentHref = window.location.href;
    },
  },
  setup() {
    //MAP
    let map: mapboxgl.Map | undefined;
    let marker: mapboxgl.Marker | undefined;
    const mapEl = ref<HTMLDivElement>();
    const coords: ComputedRef<[number, number] | undefined> = computed(() => {
      const { coordinates } = useLocation();
      return coordinates?.length ? [coordinates[0], coordinates[1]] : undefined;
    });
    const streetViewLink = computed(() =>
      coords.value
        ? `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${coords.value[1]},${coords.value[0]}`
        : ""
    );

    onMounted(async () => {
      const { location } = useLocation();
      const { settings } = useOrgSettings();
      const { baseMap } = useMap();

      if (!mapEl.value || !location || !settings) return;

      const defaultBaseMap =
        settings.mapSettings.baseMaps.find(
          (x) => x.name === settings.reportSettings?.defaultBasemapName
        ) ||
        settings.mapSettings.baseMaps.find((x) => x.isDefault) ||
        settings.mapSettings.baseMaps[0];

      const mapOpts: MapboxOptions = {
        style: await resolveBaseMapStyle(
          settings.reportSettings?.useSelectedBasemap ? baseMap : defaultBaseMap
        ),
        container: mapEl.value,
        attributionControl: false,
        interactive: false,
        zoom: 12,
        preserveDrawingBuffer: true, //required for print
      };

      map = new Map(mapOpts);
      map.on("load", () => {
        map?.resize();

        if (location.type === "Geometry") {
          const color =
            settings?.applicationSettings?.clientStyles?.color || "red";
          addPolygon(location as GeometryLocation, color);
        } else {
          setMarker(coords.value);
        }

        if (map && settings.reportSettings?.displayLayers?.length) {
          addLayers(map, settings.reportSettings.displayLayers);
        }
      });
    });

    function addLayers(map: Map, displayLayers: string[]) {
      const { settings } = useOrgSettings();
      if (!settings) return;

      const { layers, sources } = settings.mapSettings;

      const displayLayersSet = new Set(displayLayers);
      const layerSettings = layers.filter((x) => displayLayersSet.has(x.id));

      for (let layer of layerSettings) {
        // add source
        const sourceName = layer.source as string;
        const source = sources[sourceName];
        if (!source) continue;

        map.addSource(sourceName, source as AnySourceData);

        // add layer
        map.addLayer(layer as AnyLayer);
      }
    }

    function addPolygon(geomLocation: GeometryLocation, color: string) {
      if (!map) return;

      const locGeomId = "locationGeom";

      map.addSource(locGeomId, {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: geomLocation.geometry,
          properties: {},
        },
      });

      map.addLayer({
        id: locGeomId,
        source: locGeomId,
        type: "fill",
        paint: {
          "fill-color": color,
          "fill-opacity": 0.2,
          "fill-outline-color": color,
        },
      });

      map.fitBounds(bbox(geomLocation.geometry) as LngLatBoundsLike, {
        padding: 15,
        animate: false,
      });
    }

    function setMarker(coords: [number, number] | undefined) {
      if (!map || !coords || !coords.length) return;

      if (marker) {
        marker.setLngLat(coords);
      } else {
        marker = new Marker().setLngLat(coords).addTo(map);
      }
      map.flyTo({
        center: coords,
        animate: false,
        zoom: 14,
      });
      setTimeout(() => map?.resize(), 1000);
    }

    return {
      mapEl,
      streetViewLink,
    };
  },
});
